"use client";

import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import {
  format,
  addMonths,
  isSameDay,
  isWithinInterval,
  differenceInDays,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isBefore,
  getDay,
  parseISO,
  startOfDay,
} from "date-fns";
import {
  useVehicleAvailabilityCalendar,
  type CalendarDayData,
} from "../hooks/useVehicleAvailabilityCalendar";

export interface VehicleAvailabilityCalendarProps {
  /** API key for authentication */
  apiKey: string;
  /** Vehicle ID to fetch availability for */
  vehicleId: string;
  /** Fleet ID */
  fleetId: string;
  /** API base URL (optional) */
  apiBaseUrl?: string;
  /** Initial selected start date (from URL params) */
  initialFrom?: string;
  /** Initial selected end date (from URL params) */
  initialTo?: string;
  /** Callback when date range changes */
  onDateRangeChange?: (
    from: string | undefined,
    to: string | undefined,
  ) => void;
  /** Minimum number of nights required */
  minNights?: number;
  /** Maximum number of nights allowed */
  maxNights?: number;
  /**
   * Real per-night amounts of the selected stay (YYYY-MM-DD -> amount), from
   * the pricing breakdown. The grid rate carries no length-of-stay modifiers,
   * so nights of the selected range use this amount; every other date, and any
   * night missing here, keeps the grid rate.
   */
  stayDailyRates?: ReadonlyMap<string, number> | null;
}

export function VehicleAvailabilityCalendar({
  apiKey,
  vehicleId,
  fleetId,
  apiBaseUrl,
  initialFrom,
  initialTo,
  onDateRangeChange,
  minNights = 1,
  maxNights = 30,
  stayDailyRates,
}: VehicleAvailabilityCalendarProps) {
  // Initialize base date to show the month containing the initial from date, or current month
  const getInitialBaseDate = useCallback(() => {
    if (initialFrom) {
      return startOfMonth(parseISO(initialFrom));
    }
    return startOfMonth(new Date());
  }, [initialFrom]);

  const [baseDate, setBaseDate] = useState(getInitialBaseDate);

  // Initialize date range from props
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>(() => ({
    from: initialFrom ? parseISO(initialFrom) : undefined,
    to: initialTo ? parseISO(initialTo) : undefined,
  }));

  // Sync with prop changes (e.g., URL params changed externally)
  useEffect(() => {
    const newFrom = initialFrom ? parseISO(initialFrom) : undefined;
    const newTo = initialTo ? parseISO(initialTo) : undefined;

    // Only update if values actually changed
    const fromChanged =
      (newFrom && !dateRange.from) ||
      (!newFrom && dateRange.from) ||
      (newFrom && dateRange.from && !isSameDay(newFrom, dateRange.from));

    const toChanged =
      (newTo && !dateRange.to) ||
      (!newTo && dateRange.to) ||
      (newTo && dateRange.to && !isSameDay(newTo, dateRange.to));

    if (fromChanged || toChanged) {
      setDateRange({ from: newFrom, to: newTo });

      // Also update base date to show the selected range
      if (newFrom) {
        setBaseDate(startOfMonth(newFrom));
      }
    }
  }, [initialFrom, initialTo]);

  // Fetch calendar data
  const { calendarData, unavailableRanges, isLoading, isError } =
    useVehicleAvailabilityCalendar({
      apiKey,
      vehicleId,
      fleetId,
      apiBaseUrl,
      baseDate,
      monthsToFetch: 2,
      enabled: !!vehicleId && !!fleetId,
    });

  // Navigation handlers
  const handlePrevMonth = () => {
    setBaseDate((prev) => addMonths(prev, -1));
  };

  const handleNextMonth = () => {
    setBaseDate((prev) => addMonths(prev, 1));
  };

  // Check if a date is unavailable
  const isDateUnavailable = useCallback(
    (date: Date): boolean => {
      const dateStr = format(date, "yyyy-MM-dd");

      // Check calendar data first (from inventory grid)
      const dayData = calendarData.get(dateStr);
      if (dayData && !dayData.available) {
        return true;
      }

      // Check unavailable ranges
      for (const range of unavailableRanges) {
        const rangeStart = parseISO(range.start_date);
        const rangeEnd = parseISO(range.end_date);

        if (
          isWithinInterval(date, { start: rangeStart, end: rangeEnd }) ||
          isSameDay(date, rangeStart) ||
          isSameDay(date, rangeEnd)
        ) {
          return true;
        }
      }

      // Don't allow selecting past dates
      if (isBefore(startOfDay(date), startOfDay(new Date()))) {
        return true;
      }

      return false;
    },
    [calendarData, unavailableRanges],
  );

  // Check if selecting a date would create a valid range
  const wouldCreateValidRange = useCallback(
    (clickedDate: Date): boolean => {
      if (!dateRange.from) return true;
      if (dateRange.to) return true; // Starting new selection

      const start = dateRange.from;
      const end = clickedDate;

      // Check if any date in the range is unavailable
      const days = eachDayOfInterval({ start, end });
      for (const day of days) {
        if (isDateUnavailable(day)) {
          return false;
        }
      }

      // Check min/max nights
      const nights = differenceInDays(end, start);
      if (nights < minNights || nights > maxNights) {
        return false;
      }

      return true;
    },
    [dateRange, isDateUnavailable, minNights, maxNights],
  );

  // Handle date click
  const handleDateClick = useCallback(
    (date: Date) => {
      // Don't allow clicking unavailable dates
      if (isDateUnavailable(date)) {
        return;
      }

      let newFrom: Date | undefined;
      let newTo: Date | undefined;

      if (!dateRange.from || (dateRange.from && dateRange.to)) {
        newFrom = date;
        newTo = undefined;
      } else if (dateRange.from && !dateRange.to) {
        if (isBefore(date, dateRange.from)) {
          newFrom = date;
          newTo = undefined;
        } else if (!wouldCreateValidRange(date)) {
          newFrom = date;
          newTo = undefined;
        } else {
          newFrom = dateRange.from;
          newTo = date;
        }
      }

      setDateRange({ from: newFrom, to: newTo });

      if (onDateRangeChange) {
        const fromStr = newFrom ? format(newFrom, "yyyy-MM-dd") : undefined;
        const toStr = newTo ? format(newTo, "yyyy-MM-dd") : undefined;
        onDateRangeChange(fromStr, toStr);
      }
    },
    [dateRange, isDateUnavailable, wouldCreateValidRange, onDateRangeChange],
  );

  const getDatePrice = useCallback(
    (date: Date): number | null => {
      const dateStr = format(date, "yyyy-MM-dd");

      // A night of the selected stay: show what that night really costs.
      // The check-out day is not a charged night, so it has no entry and
      // keeps the grid rate like every date outside the selection.
      if (
        stayDailyRates &&
        dateRange.from &&
        dateRange.to &&
        !isBefore(date, startOfDay(dateRange.from)) &&
        isBefore(date, startOfDay(dateRange.to))
      ) {
        const stayRate = stayDailyRates.get(dateStr);
        if (stayRate !== undefined) return stayRate;
      }

      const dayData = calendarData.get(dateStr);
      return dayData?.rate ?? null;
    },
    [calendarData, stayDailyRates, dateRange.from, dateRange.to],
  );

  // Render a single month
  const renderMonth = (monthStart: Date) => {
    const monthEnd = endOfMonth(monthStart);
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDayOfWeek = getDay(monthStart);

    return (
      <div className="flex-1">
        <h4 className="font-semibold text-center mb-3 text-[hsl(222.2_84%_4.9%)]">
          {format(monthStart, "MMMM yyyy")}
        </h4>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((d) => (
            <div
              key={d}
              className="text-center text-xs text-[hsl(215.4_16.3%_46.9%)] font-medium py-1"
            >
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {/* Empty slots for previous month days */}
          {Array.from({ length: startDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="h-14" />
          ))}

          {monthDays.map((day) => {
            const price = getDatePrice(day);
            const unavailable = isDateUnavailable(day);

            const isSelected = dateRange.from && isSameDay(day, dateRange.from);
            const isEnd = dateRange.to && isSameDay(day, dateRange.to);
            const inRange =
              dateRange.from &&
              dateRange.to &&
              isWithinInterval(day, {
                start: dateRange.from,
                end: dateRange.to,
              }) &&
              !isSelected &&
              !isEnd;

            // Determine styling based on status
            let bgStyle: React.CSSProperties = {};
            let textColor = "#020817";
            let priceColor = "#64748B";
            let cursorStyle = "pointer";

            if (unavailable) {
              // Unavailable: gray background
              bgStyle = { backgroundColor: "#F3F4F6" };
              textColor = "#9CA3AF";
              priceColor = "#9CA3AF";
              cursorStyle = "not-allowed";
            } else if (isSelected || isEnd) {
              // Selected: solid primary blue
              bgStyle = { backgroundColor: "#083C91" };
              textColor = "#FFFFFF";
              priceColor = "rgba(255,255,255,0.8)";
            } else if (inRange) {
              // In Range: primary blue at 20% opacity
              bgStyle = { backgroundColor: "rgba(8, 60, 145, 0.2)" };
              textColor = "#020817";
              priceColor = "#64748B";
            }

            return (
              <button
                key={day.toISOString()}
                onClick={() => handleDateClick(day)}
                disabled={unavailable}
                type="button"
                className={`h-14 w-full flex flex-col items-center justify-center rounded-md text-sm transition-colors relative ${
                  unavailable ? "" : "hover:bg-blue-50"
                }`}
                style={{ ...bgStyle, cursor: cursorStyle }}
              >
                <span className="font-medium" style={{ color: textColor }}>
                  {format(day, "d")}
                </span>
                {price !== null ? (
                  <span className="text-[10px]" style={{ color: priceColor }}>
                    ${price.toFixed(2)}
                  </span>
                ) : (
                  <span
                    className="text-[10px]"
                    style={{ color: priceColor, opacity: 0.5 }}
                  >
                    -
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const nights =
    dateRange.from && dateRange.to
      ? differenceInDays(dateRange.to, dateRange.from)
      : 0;

  if (isError) {
    return (
      <div className="space-y-4 mb-8">
        <p className="text-red-500 text-sm">
          Failed to load availability data. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 mb-8">
      <p className="text-[#64748B] text-sm font-medium">
        Select your dates to see availability and pricing
      </p>

      {/* Navigation Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevMonth}
          className="p-2 rounded-full hover:bg-blue-50 transition-colors"
          type="button"
        >
          <ChevronLeft className="h-5 w-5 text-[#64748B]" />
        </button>
        <span className="text-sm font-medium text-[#64748B]">
          {format(baseDate, "MMM yyyy")} -{" "}
          {format(addMonths(baseDate, 1), "MMM yyyy")}
        </span>
        <button
          onClick={handleNextMonth}
          className="p-2 rounded-full hover:bg-blue-50 transition-colors"
          type="button"
        >
          <ChevronRight className="h-5 w-5 text-[#020817]" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6">
          {renderMonth(baseDate)}
          {renderMonth(addMonths(baseDate, 1))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-[#64748B] pt-2 border-t border-gray-200">
        <div className="flex items-center gap-1">
          <div
            className="w-3 h-3 rounded"
            style={{ backgroundColor: "#083C91" }}
          ></div>
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-1">
          <div
            className="w-3 h-3 rounded"
            style={{ backgroundColor: "rgba(8, 60, 145, 0.2)" }}
          ></div>
          <span>In Range</span>
        </div>
        <div className="flex items-center gap-1">
          <div
            className="w-3 h-3 rounded"
            style={{ backgroundColor: "#F3F4F6" }}
          ></div>
          <span>Unavailable</span>
        </div>
      </div>

      {/* Selected Range Info / Footer */}
      <div className="p-3 bg-gray-50 rounded-lg text-sm">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[#64748B]" />
          <span className="text-[#020817]">
            {dateRange.from
              ? format(dateRange.from, "MMM d, yyyy")
              : "Select dates"}{" "}
            {dateRange.to && (
              <>
                <span className="mx-1">-</span>
                {format(dateRange.to, "MMM d, yyyy")}
              </>
            )}
            {nights > 0 && (
              <span className="text-[#64748B] ml-1">
                ({nights} {nights === 1 ? "night" : "nights"})
              </span>
            )}
            {!dateRange.from && "Select check-in date"}
          </span>
        </div>
      </div>
    </div>
  );
}
